import type { ReactNode } from 'react';
import { useState } from 'react';
import { Icon, type IconName } from './Icon';

/**
 * 基础展示组件。
 *
 * 规格来自阿里云控制台的实拍取数：控件高 40px、圆角 8px，卡片白底**无边线**，
 * 靠白底压住内容区的软蓝渐变分层；卡内再嵌一层浅蓝块（`NestBlock`）承载子卡片。
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'md' | 'sm';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // 操作面一律实心品牌蓝：控制台内不用渐变，那是营销面的语言。
  primary: 'bg-brand text-white hover:bg-brand-hover disabled:bg-brand/40',
  secondary: 'bg-panel text-fg border border-line-strong hover:border-brand hover:text-brand',
  ghost: 'text-fg-muted hover:bg-nest-2 hover:text-fg',
  danger: 'bg-danger text-white hover:opacity-90',
  link: 'text-brand hover:underline px-0',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  md: 'h-10 px-4 text-sm',
  sm: 'h-8 px-3 text-[13px]',
};

export interface ButtonProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  type?: 'button' | 'submit';
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: IconName;
  /** 撑满一行，用于表单主按钮与移动端 */
  block?: boolean;
  className?: string;
  title?: string;
}

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  type = 'button',
  onClick,
  disabled = false,
  loading = false,
  icon,
  block = false,
  className = '',
  title,
}: ButtonProps) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-control font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      className={`${base} ${BUTTON_VARIANTS[variant]} ${variant === 'link' ? '' : BUTTON_SIZES[size]} ${block ? 'w-full' : ''} ${className}`}
    >
      {loading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : icon ? (
        <Icon name={icon} className="h-4 w-4" />
      ) : null}
      {children}
    </button>
  );
}

export type BadgeTone = 'neutral' | 'brand' | 'ok' | 'danger' | 'warn' | 'info';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-nest-2 text-fg-muted',
  brand: 'bg-brand-soft text-brand',
  ok: 'bg-ok-soft text-ok',
  danger: 'bg-danger-soft text-danger',
  warn: 'bg-warn-soft text-warn',
  info: 'bg-info-soft text-info',
};

export function Badge({
  children,
  tone = 'neutral',
  className = '',
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${BADGE_TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

export function Card({
  children,
  className = '',
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={`rounded-card bg-panel ${padded ? 'p-5' : ''} ${className}`}>{children}</section>
  );
}

/**
 * 卡片头：标题 + 可选帮助提示 + 右上角动作。
 *
 * 「查看详情 ↗」与「?」提示是阿里云控制台每个卡片都有的两个位置，
 * 因此固定成组件而不是每页各写一遍。
 */
export function CardHeader({
  title,
  hint,
  action,
  className = '',
}: {
  title: ReactNode;
  /** 悬停显示的解释文本，渲染成原生 title 之外还有可见的「?」按钮 */
  hint?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`mb-4 flex flex-wrap items-center justify-between gap-2 ${className}`}>
      <h2 className="flex items-center gap-1.5 text-[15px] font-semibold text-fg">
        {title}
        {hint ? (
          <span
            className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-nest-2 text-[10px] text-fg-subtle"
            title={hint}
            aria-label={hint}
          >
            ?
          </span>
        ) : null}
      </h2>
      {action}
    </div>
  );
}

/** 卡内嵌的浅蓝块，用来再放一层白色子卡片（阿里云控制台的两级嵌套）。 */
export function NestBlock({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`rounded-control bg-nest p-3 ${className}`}>{children}</div>;
}

export function EmptyState({
  title,
  description,
  action,
  icon = 'list',
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-control bg-nest-2 px-6 py-12 text-center">
      <Icon name={icon} className="h-6 w-6 text-fg-subtle" />
      <p className="text-sm font-medium text-fg">{title}</p>
      {description ? <p className="max-w-md text-[13px] text-fg-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-nest-2 ${className}`} />;
}

/** 一组骨架，用于表格与卡片列表的首屏。 */
export function SkeletonList({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold text-fg">{title}</h1>
        {description ? <p className="mt-1 text-[13px] text-fg-muted">{description}</p> : null}
      </div>
      {action}
    </header>
  );
}

export function StatBlock({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'default' | 'ok' | 'danger' | 'warn';
}) {
  const toneClass =
    tone === 'ok' ? 'text-ok' : tone === 'danger' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-fg';

  return (
    <div className="rounded-control bg-nest-2 px-4 py-3">
      <p className="flex items-center gap-1 text-xs text-fg-muted">
        {label}
        {hint ? <span className="cursor-help text-fg-subtle" title={hint}>?</span> : null}
      </p>
      <p className={`tabular mt-1 text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

/** 带「复制」反馈的只读字段，用于密钥、邀请链接这类只出现一次的值。 */
export function CopyField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[13px] font-medium text-fg">{label}</span>
        <Button size="sm" variant="link" icon={copied ? 'check' : 'copy'} onClick={copy}>
          {copied ? '已复制' : '复制'}
        </Button>
      </div>
      <code className="block break-all rounded-control bg-nest px-3 py-2 font-mono text-[12px] text-fg">
        {value}
      </code>
      {hint ? <p className="mt-1 text-xs text-fg-muted">{hint}</p> : null}
    </div>
  );
}
